import React, { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { confirmOnRequestAppointment, declineOnRequestAppointment } from '../../services/appointmentService';
import { formatCalendarDate, formatClockTime } from '../../utils/dateFormatUtils';

const AppointmentConfirmationAlertDialog = ({ alert, onClose, storeId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const resolvedStoreId = alert?.storeId || storeId;
  const appointmentId = alert?.appointmentId;

  const respond = async (action) => {
    if (!appointmentId || !resolvedStoreId) {
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      if (action === 'confirm') {
        await confirmOnRequestAppointment(appointmentId, resolvedStoreId);
      } else {
        await declineOnRequestAppointment(appointmentId, resolvedStoreId);
      }
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.onRequestRespondError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScrollableDialog
      open={Boolean(alert)}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('appointmentConfirmationAlert.title')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={() => respond('decline')} disabled={submitting}>
            {t('appointmentConfirmationAlert.decline')}
          </Button>
          <PrimaryActionButton onClick={() => respond('confirm')} disabled={submitting}>
            {t('appointmentConfirmationAlert.confirm')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Stack spacing={1.5}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography>
          <strong>{t('appointmentConfirmationAlert.customer')}:</strong> {alert?.customerName}
        </Typography>
        <Typography>
          {formatCalendarDate(alert?.date)} {formatClockTime(alert?.begin)}–{formatClockTime(alert?.end)}
        </Typography>
        {alert?.openToAnyProfessional && (
          <Typography color="text.secondary">
            {t('appointmentConfirmationAlert.openToAny')}
          </Typography>
        )}
        <Button
          color="inherit"
          onClick={() => {
            onClose?.();
            if (!appointmentId) {
              return;
            }
            const params = new URLSearchParams({ appointment: String(appointmentId) });
            const date = alert?.date ? String(alert.date).slice(0, 10) : '';
            if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
              params.set('date', date);
            }
            navigate(`/management/appointments?${params.toString()}`);
          }}
          sx={{ alignSelf: 'flex-start', px: 0 }}
        >
          {t('appointmentConfirmationAlert.viewAppointment')}
        </Button>
      </Stack>
    </ScrollableDialog>
  );
};

export default AppointmentConfirmationAlertDialog;
